import React, { useEffect, useState } from 'react'
import { addDays, endOfDay, isToday, startOfDay, subDays, isWeekend } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, useTheme } from '@mui/material'
import { grey } from '@mui/material/colors'

import { ExtendedWarrantEntity, getEntityDates, getEntitySize, getMonthName } from './utils'
import { IssueEntity } from '../../types/IssueEntity'
import { CalendarTableRow } from './CalendarTableRow'
import { NAVBAR_DRAWER_WIDTH } from '../../layouts/DashboardBase'

type CalendarTableComponent = React.FC<{
    filteredEntities: Array<IssueEntity | ExtendedWarrantEntity>
}>

const LEFT_COLUMN_WIDTH = 280

export const CalendarTable: CalendarTableComponent = ({ filteredEntities }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme()
    const [mouseOverColIndex, setMouseOverColIndex] = useState<number | null>(null)
    const todayCellRef = React.useRef<HTMLTableCellElement>(null)

    const today = React.useMemo(() => new Date(), [])
    const startDate = React.useMemo(() => subDays(startOfDay(today), 30), [today])
    const endDate = React.useMemo(() => addDays(endOfDay(today), 60), [today])

    const isSmallScreen = useMediaQuery('(max-width: 960px)')

    const navBarDrawerWidth = isSmallScreen ? 0 : NAVBAR_DRAWER_WIDTH

    useEffect(() => {
        if (!todayCellRef.current) return

        todayCellRef.current.scrollIntoView({ inline: 'start', block: 'nearest' })
    }, [])

    const datesList = React.useMemo(() => {
        const datesList: Date[] = []

        const daysBetweenSubStartAndSubEnd = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)

        for (let index = 0; index < daysBetweenSubStartAndSubEnd; index++) {
            const date = startDate
            const dateToAdd = addDays(date, index)
            datesList.push(dateToAdd)
        }

        return datesList
    }, [endDate, startDate])

    const monthsList = React.useMemo(() => {
        return datesList.reduce((months: { monthNumber: number; occurences: number }[], date) => {
            const alreadyExists = months.findIndex((month) => month.monthNumber === date.getMonth())
            if (alreadyExists >= 0) {
                const existingMonth = months[alreadyExists]
                months[alreadyExists] = { ...existingMonth, occurences: existingMonth.occurences + 1 }
            } else {
                months.push({ monthNumber: date.getMonth(), occurences: 1 })
            }

            return months
        }, [])
    }, [datesList])

    const handleMouseLeave = React.useCallback(() => {
        setMouseOverColIndex(null)
    }, [])

    const handleMouseOver = React.useCallback(
        (event: React.MouseEvent<HTMLTableCellElement>) => {
            const colClass = event.currentTarget.classList.value.split(' ').find((c) => c.startsWith('col-'))

            if (colClass) {
                setMouseOverColIndex(+colClass.replace('col-', '') - 1)
            }
        },
        [setMouseOverColIndex],
    )

    const tableBody = React.useMemo(
        () => (
            <TableBody>
                {filteredEntities.length &&
                    filteredEntities.map((entity) => {
                        const {
                            isSubStartDateBeforeCalendarStart,
                            isSubEndDateBeforeCalendarStart,
                            isSubEndDateAfterCalendarEnd,
                            subStartDate,
                            subEndDate,
                            subLabelDate,
                        } = getEntityDates(entity, startDate, endDate)

                        const { size, showPrelPrice } = getEntitySize(entity, startDate, locale)

                        return (
                            <CalendarTableRow
                                key={entity.id}
                                datesList={datesList}
                                entity={entity}
                                onMouseLeave={handleMouseLeave}
                                onMouseOver={handleMouseOver}
                                isSubStartDateBeforeCalendarStart={!!isSubStartDateBeforeCalendarStart}
                                isSubEndDateBeforeCalendarStart={!!isSubEndDateBeforeCalendarStart}
                                isSubEndDateAfterCalendarEnd={!!isSubEndDateAfterCalendarEnd}
                                subStartDate={subStartDate}
                                subEndDate={subEndDate}
                                subLabelDate={subLabelDate}
                                size={size}
                                showPrelPrice={showPrelPrice}
                            />
                        )
                    })}
            </TableBody>
        ),
        [datesList, endDate, filteredEntities, handleMouseOver, locale, startDate],
    )

    return (
        <Table
            className='calendar-table'
            stickyHeader
            sx={{
                [`thead tr:nth-of-type(2) th:nth-of-type(${mouseOverColIndex})`]: {
                    backgroundColor: theme.palette.secondary.dark,
                    color: theme.palette.secondary.contrastText,
                },
            }}
        >
            <TableHead>
                <TableRow sx={{ position: 'relative', zIndex: 3 }}>
                    <TableCell
                        id='issue_calendar_header_cell_company_name'
                        rowSpan={2}
                        sx={{
                            top: '54px',
                            minWidth: `${LEFT_COLUMN_WIDTH}px`,
                            background: 'white',
                            zIndex: 3,
                            padding: '0px',
                        }}
                    >
                        <Box sx={{ height: '67px', borderRight: `1px solid ${grey[300]}` }} />
                    </TableCell>

                    {monthsList.map((month, i) => (
                        <TableCell
                            key={month.monthNumber}
                            colSpan={month.occurences}
                            align='center'
                            sx={{
                                top: '60px',
                                minWidth: '56px',
                                padding: '6px 10px',
                                background: 'white',
                                borderLeft: i === 0 ? 'none' : `1px solid ${grey[300]}`,
                                borderRight: i === monthsList.length ? `1px solid ${grey[300]}` : null,
                            }}
                        >
                            {getMonthName(month.monthNumber)}
                        </TableCell>
                    ))}
                </TableRow>
                <TableRow>
                    {datesList.map((date, i) => {
                        const isTodayDate = isToday(date)
                        return (
                            <TableCell
                                key={date.toISOString()}
                                align='center'
                                id={isTodayDate ? 'date_today' : date.toISOString()}
                                ref={isTodayDate ? todayCellRef : null}
                                sx={{
                                    scrollMarginLeft: `${navBarDrawerWidth + LEFT_COLUMN_WIDTH + 66}px`,
                                    top: '95px',
                                    width: '56px',
                                    padding: '4px 10px',
                                    background: isWeekend(date) ? grey[200] : 'white',
                                    borderLeft: i === 0 ? 'none' : `1px solid ${grey[300]}`,
                                    borderRight: i === datesList.length ? `1px solid ${grey[300]}` : null,
                                }}
                            >
                                <div
                                    style={
                                        isToday(date)
                                            ? {
                                                  backgroundColor: theme.palette.secondary.dark,
                                                  color: theme.palette.secondary.contrastText,
                                                  borderRadius: '50%',
                                                  aspectRatio: '1 / 1',
                                                  display: 'flex',
                                                  justifyContent: 'center',
                                                  alignItems: 'center',
                                                  lineHeight: '1',
                                              }
                                            : {}
                                    }
                                    title={isToday(date) ? 'Idag' : ''}
                                >
                                    {date.getDate()}
                                </div>
                            </TableCell>
                        )
                    })}
                </TableRow>
            </TableHead>
            {tableBody}
        </Table>
    )
}
